import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Truck, ShieldCheck, Smartphone } from "lucide-react";
import { api, BANNERS, CATEGORY_IMAGES } from "../lib/api";
import { ProductCard } from "../components/ProductCard";
import { CommunityBanner, BusinessBanner, AppPromo } from "../components/Promos";

const Hero = () => {
  const [i, setI] = useState(0);
  useEffect(() => { const t = setInterval(() => setI((x) => (x + 1) % BANNERS.length), 6000); return () => clearInterval(t); }, []);
  return (
    <section className="relative h-[520px] sm:h-[600px] overflow-hidden" data-testid="hero-carousel">
      {BANNERS.map((b, idx) => (
        <div key={idx} className={`absolute inset-0 transition-opacity duration-1000 ${idx === i ? "opacity-100" : "opacity-0"}`}>
          <img src={b.img} alt={b.title} className={`w-full h-full object-cover ${idx === i ? "animate-kenburns" : ""}`} />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1A1714]/85 via-[#1A1714]/45 to-transparent" />
          <div className="absolute inset-0 max-w-7xl mx-auto px-6 flex flex-col justify-center">
            {idx === i && (
              <div className="max-w-xl text-white animate-rise">
                <span className="text-[#D99B26] text-xs font-bold uppercase tracking-[0.3em]">{b.kicker}</span>
                <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] mt-4">{b.title}</h1>
                <p className="mt-5 text-base text-stone-200 max-w-md">{b.sub}</p>
                <Link to={`/shop?category=${b.cta}`} data-testid={`hero-cta-${idx}`} className="inline-flex items-center gap-2 mt-8 bg-[#C85A32] hover:bg-[#A9481F] px-7 h-12 rounded-full font-semibold transition-colors">
                  Shop now <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            )}
          </div>
        </div>
      ))}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2">
        {BANNERS.map((_, idx) => <button key={idx} data-testid={`hero-dot-${idx}`} onClick={() => setI(idx)} className={`h-1.5 rounded-full transition-[width,background-color] ${idx === i ? "w-10 bg-[#D99B26]" : "w-4 bg-white/50"}`} />)}
      </div>
    </section>
  );
};

const Perks = () => (
  <div className="max-w-7xl mx-auto px-6 -mt-10 relative z-10 grid sm:grid-cols-3 gap-4">
    {[[Smartphone, "Mobile money ready", "M-Pesa, EcoCash, MoMo"], [Truck, "Delivered across Africa", "Free over $50"], [ShieldCheck, "Buyer protection", "Secure checkout"]].map(([I, t, s]) => (
      <div key={t} className="bg-white rounded-2xl p-5 flex gap-4 items-center shadow-[0_10px_40px_-15px_rgba(26,23,20,0.25)]">
        <div className="w-11 h-11 rounded-full bg-[#1E5E3A]/10 grid place-items-center"><I className="w-5 h-5 text-[#1E5E3A]" /></div>
        <div><p className="font-semibold text-sm">{t}</p><p className="text-xs text-stone-500">{s}</p></div>
      </div>
    ))}
  </div>
);

const CategoryTiles = ({ cats }) => (
  <section className="max-w-7xl mx-auto px-6 mt-20">
    <h2 className="font-display text-lg md:text-lg font-bold uppercase tracking-[0.2em] text-[#C85A32]">Shop by category</h2>
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6">
      {cats.map((c) => (
        <Link key={c.slug} to={`/shop?category=${c.slug}`} data-testid={`category-tile-${c.slug}`}
          className="group relative rounded-2xl overflow-hidden h-44 sm:h-52 text-white">
          <img src={CATEGORY_IMAGES[c.slug]} alt={c.name} loading="lazy" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1A1714]/85 via-[#1A1714]/20 to-transparent" />
          <div className="absolute bottom-0 p-4">
            <span className="font-display text-xl font-extrabold block">{c.name}</span>
            <span className="text-xs opacity-90 flex items-center gap-1">{c.count} items <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" /></span>
          </div>
        </Link>
      ))}
    </div>
  </section>
);

const Row = ({ cat, title }) => {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/products", { params: { category: cat, limit: 10, sort: "rating" } }).then((r) => setItems(r.data.items)); }, [cat]);
  if (!items.length) return null;
  return (
    <section className="max-w-7xl mx-auto px-6 mt-16" data-testid={`home-row-${cat}`}>
      <div className="flex items-end justify-between">
        <h2 className="font-display text-2xl sm:text-3xl font-extrabold">{title}</h2>
        <Link to={`/shop?category=${cat}`} className="text-sm font-semibold text-[#C85A32] hover:underline">View all</Link>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mt-6">
        {items.map((p) => <ProductCard key={p.id} p={p} />)}
      </div>
    </section>
  );
};

export default function Home() {
  const [cats, setCats] = useState([]);
  useEffect(() => { api.get("/categories").then((r) => setCats(r.data)); }, []);
  const rows = cats.filter((c) => c.count > 0);
  return (
    <div data-testid="home-page">
      <Hero />
      <Perks />
      <CategoryTiles cats={cats} />
      {rows.slice(0, 1).map((c) => <Row key={c.slug} cat={c.slug} title={`Top picks in ${c.name}`} />)}
      <CommunityBanner />
      <AppPromo />
      {rows.slice(1).map((c) => <Row key={c.slug} cat={c.slug} title={`Top picks in ${c.name}`} />)}
      <BusinessBanner />
    </div>
  );
}
